import { useState, useEffect, useCallback, useRef } from "react";
import {
  NotificationService,
  Notification,
} from "../services/notification.service";

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  // Actions
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  // Filters
  showUnreadOnly: boolean;
  setShowUnreadOnly: (value: boolean) => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Pagination
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Prevent multiple concurrent requests
  const loadingRef = useRef(false);

  /**
   * Load notifications from the API
   */
  const loadNotifications = useCallback(
    async (reset: boolean = false) => {
      if (loadingRef.current) return;

      try {
        setLoading(true);
        loadingRef.current = true;
        setError(null);

        const currentOffset = reset ? 0 : offset;

        const response = await NotificationService.getNotifications(
          limit,
          currentOffset,
          showUnreadOnly
        );

        if (response.success) {
          const newNotifications = response.data;

          if (reset) {
            setNotifications(newNotifications);
            setOffset(newNotifications.length);
          } else {
            setNotifications((prev) => [...prev, ...newNotifications]);
            setOffset((prev) => prev + newNotifications.length);
          }

          setUnreadCount(response.metadata.unreadCount);
          setHasMore(response.metadata.hasMore);
          setTotalCount(response.metadata.total);
        } else {
          setError("Failed to load notifications");
        }
      } catch (err) {
        console.error("Error loading notifications:", err);

        // Provide mock notifications when API is not available
        const mockNotifications: Notification[] = [
          {
            _id: "mock1",
            title: "New Satsangi Registration",
            message: "A new satsangi has registered and is pending review",
            type: "USER_REGISTRATION",
            priority: "NORMAL",
            isRead: false,
            isDeleted: false,
            createdAt: new Date().toISOString(),
            recipientId: "current-user",
            relatedEntityType: "satsangi",
            actionUrl: "/satsangi-review",
          },
          {
            _id: "mock2",
            title: "Review Submitted",
            message: "A satsangi review has been submitted for approval",
            type: "REVIEW_SUBMITTED",
            priority: "HIGH",
            isRead: false,
            isDeleted: false,
            createdAt: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
            recipientId: "current-user",
            relatedEntityType: "review",
          },
          {
            _id: "mock3",
            title: "System Update",
            message: "The notification system has been successfully integrated",
            type: "SYSTEM_UPDATE",
            priority: "LOW",
            isRead: true,
            isDeleted: false,
            createdAt: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
            readAt: new Date(Date.now() - 1800000).toISOString(), // Read 30 min ago
            recipientId: "current-user",
          },
        ];

        setNotifications(mockNotifications);
        setUnreadCount(2); // 2 unread notifications
        setTotalCount(3);
        setHasMore(false);
        setError(null); // Clear error since we're showing mock data
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [limit, offset, showUnreadOnly]
  );

  /**
   * Refresh notifications (reload from beginning)
   */
  const refreshNotifications = useCallback(async () => {
    setOffset(0);
    await loadNotifications(true);
  }, [loadNotifications]);

  /**
   * Load more notifications (pagination)
   */
  const loadMoreNotifications = useCallback(async () => {
    if (hasMore && !loading) {
      await loadNotifications(false);
    }
  }, [hasMore, loading, loadNotifications]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await NotificationService.markAsRead(notificationId);

      if (response.success) {
        // Update local state
        setNotifications((prev) =>
          prev.map((notification) =>
            notification._id === notificationId
              ? {
                  ...notification,
                  isRead: true,
                  readAt: new Date().toISOString(),
                }
              : notification
          )
        );

        // Update unread count
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
      setError("Failed to mark notification as read");
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await NotificationService.markAllAsRead();

      if (response.success) {
        // Update local state
        setNotifications((prev) =>
          prev.map((notification) => ({
            ...notification,
            isRead: true,
            readAt: new Date().toISOString(),
          }))
        );

        // Reset unread count
        setUnreadCount(0);
      }
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      setError("Failed to mark all notifications as read");
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        const response = await NotificationService.deleteNotification(
          notificationId
        );

        if (response.success) {
          // Remove from local state
          setNotifications((prev) => {
            const filtered = prev.filter(
              (notification) => notification._id !== notificationId
            );
            return filtered;
          });

          // Update counts
          const wasUnread =
            notifications.find((n) => n._id === notificationId)?.isRead ===
            false;
          if (wasUnread) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
          setTotalCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        console.error("Error deleting notification:", err);
        setError("Failed to delete notification");
      }
    },
    [notifications]
  );

  /**
   * Load unread count periodically
   */
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await NotificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (err) {
      console.error("Error loading unread count:", err);
    }
  }, []);

  // Load notifications on mount and when filters change
  useEffect(() => {
    refreshNotifications();
  }, [showUnreadOnly, refreshNotifications]); // Only refresh when filter changes

  // Load initial data
  useEffect(() => {
    refreshNotifications();
    loadUnreadCount();
  }, [refreshNotifications, loadUnreadCount]);

  // Periodic unread count refresh (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    totalCount,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMoreNotifications,
    showUnreadOnly,
    setShowUnreadOnly,
  };
};
